import {
  AfterContentInit,
  Component,
  computed,
  ElementRef,
  inject,
  signal,
  ViewChild,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import {
  HsToolbarComponent,
  HsRailNavButtonGroupModel,
  HsRailNavModule,
  HsToolbarModule,
  HsProfileButtonDirective,
  HsIconModule,
  HsTooltipModule,
  HsMenuModule,
} from '@hsetu/ngui-components';
import { CloudWorkerSyncService } from '@shared/sync/cloud-worker-sync.service';
import { LoginResponse } from 'angular-auth-oidc-client';
import { environment } from '../environments/environment';
import { ThemingService } from '@shared/ui/theming/theming.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  ICON_LOGOUT,
  ICON_MOON,
  ICON_SETTINGS,
  ICON_SUN,
  ICON_USER,
} from '@shared/ui/icons/icon-constants';
import { AuthService } from '@shared/authentication/auth.service';
import { Dialog } from '@angular/cdk/dialog';
import { KfmContactProfileDialogComponent } from '@modules/contact/features/contact-profile-dialog/kfm-contact-profile-dialog.component';
import { KfmAppSettingsDialogComponent } from '@modules/settings/features/app-settings-dialog/kfm-app-settings-dialog.component';
import { LicenseService } from '@shared/licensing/license.service';
import { KfmSpinnerDialogComponent } from '@shared/ui/components/features/dialogs/spinner-dialog/kfm-spinner-dialog.component';
import { KfmInfoDialogComponent } from '@shared/ui/components/features/dialogs/info-dialog/kfm-info-dialog.component';
import { AppStage } from '@shared/enums/app-state.enum';
import { ChatbotService } from '@shared/tools/chatbot.service';
import { RailNavService } from '@shared/ui/services/rail-nav.service';
import { LOGIN_PAGE, HOME_PAGE } from '@shared/constants/routing-constants';
import { KaufmannSettingsService } from '@modules/settings/services/kfm-settings.service';
import { DataMigrationService } from '@modules/sales/services/data-migration.service';

@Component({
  imports: [
    RouterModule,
    HsToolbarComponent,
    HsRailNavModule,
    HsToolbarModule,
    HsProfileButtonDirective,
    HsIconModule,
    HsTooltipModule,
    HsMenuModule,
  ],
  selector: 'kfm-root',
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App implements AfterContentInit {
  private readonly _router = inject(Router);
  private readonly _cloudWorkerSyncService = inject(CloudWorkerSyncService);
  private readonly _railNavService = inject(RailNavService);
  private readonly _sanitizer = inject(DomSanitizer);
  private readonly _authService = inject(AuthService);
  private readonly _themeService = inject(ThemingService);
  private readonly _licenseService = inject(LicenseService);
  private readonly _dialog = inject(Dialog);
  private readonly _isDarkMode = signal<boolean>(false);
  private readonly _chatBotService = inject(ChatbotService);
  private readonly _settingsService = inject(KaufmannSettingsService);
  private readonly _dataMigrationService = inject(DataMigrationService);

  @ViewChild('railNav', { static: true, read: ElementRef })
  protected railNav!: ElementRef;

  protected readonly title = 'E-Kaufmann';
  protected readonly settingsLabel = 'Einstellungen';
  protected readonly profileLabel = 'Meine Daten';
  protected readonly logoutLabel = 'Logout';

  protected readonly toggleThemeTooltip = 'Toggle Theme';
  protected readonly settingsIcon =
    this._sanitizer.bypassSecurityTrustHtml(ICON_SETTINGS);
  protected readonly iconLogout =
    this._sanitizer.bypassSecurityTrustHtml(ICON_LOGOUT);
  protected readonly iconProfile =
    this._sanitizer.bypassSecurityTrustHtml(ICON_USER);

  protected themeIcon = computed<SafeHtml>(() => {
    const isDarkMode = this._isDarkMode();
    return isDarkMode
      ? this._sanitizer.bypassSecurityTrustHtml(ICON_MOON)
      : this._sanitizer.bypassSecurityTrustHtml(ICON_SUN);
  });

  protected readonly userInitials = signal<string>('');

  protected readonly isAuthenticated = signal<boolean>(false);

  protected railNavButtonGroups: HsRailNavButtonGroupModel[] = [];
  protected railNavBottomGroup: HsRailNavButtonGroupModel;

  constructor() {
    if (
      window.location.href !== window.location.origin &&
      !window.location.href.includes(LOGIN_PAGE)
    ) {
      this._router.navigateByUrl(window.location.origin);
    }
  }

  async ngAfterContentInit(): Promise<void> {
    this._themeService.isDarkMode().then((isDarkMode) => {
      this._isDarkMode.set(isDarkMode);
    });

    this._authService.isAuthenticated$.subscribe(
      async (loginResponse: LoginResponse) => {
        if (loginResponse && loginResponse.isAuthenticated) {
          // Check license
          // const licenseValid = await this.checkLicense();

          // if (!licenseValid) {
          //   return;
          // }

          this.isAuthenticated.set(true);

          // show a spinner for the entire initialization phase
          const initSpinnerRef = this._dialog.open(KfmSpinnerDialogComponent, {
            data: { text: 'Anwendung wird initialisiert...' },
          });

          try {
            // get current tenant
            await this.checkAndInitializeSelectedTenant();

            // get user initials
            const initials = await this._authService.getProfileInitials();
            this.userInitials.set(initials);

            // start sync
            const kaufmannFinancialReady =
              this._cloudWorkerSyncService.waitForDomainSyncReady(
                ['Kaufmann', 'Financial'],
                180000,
              );
            this._cloudWorkerSyncService.startDomainSyncs();

            // wait until both domains have completed (or failed) their first sync cycle first.
            await kaufmannFinancialReady;

            // run Data Migrations
            await this._dataMigrationService.migrateData();

            // start chat bot
            await this._chatBotService.start();
          } finally {
            initSpinnerRef.close();
          }

          const navResult = this._railNavService.build();
          this.railNavButtonGroups = navResult.groups;
          this.railNavBottomGroup = navResult.bottomGroup;

          this._railNavService.showRailNav(this.railNav);

          this._router.navigate([HOME_PAGE]);
        } else {
          this.isAuthenticated.set(false);

          // Hide rail nav
          this._railNavService.hideRailNav(this.railNav);

          // Stop sync
          this._cloudWorkerSyncService.stopDomainSyncs();

          // Redirect to login page
          this._router.navigate([LOGIN_PAGE]);
        }
      },
    );
  }

  /** Theming button clicked */
  protected async onThemingChanged(): Promise<void> {
    await this._themeService.toggleTheme();
    const isDarkMode = await this._themeService.isDarkMode();
    this._isDarkMode.set(isDarkMode);
  }

  /** Logout button clicked */
  protected onLogoutClicked() {
    this._authService.logout(environment.auth.configId).subscribe(() => {
      console.log('App component - logoffAndRevokeTokens');
    });
  }

  /** Profile button clicked */
  protected onProfileClicked() {
    this._dialog.open(KfmContactProfileDialogComponent, {
      width: '600px',
      height: '100%',
      maxHeight: '90vh',
      maxWidth: '90vw',
    });
  }

  /** Settings button clicked */
  protected onSettingsClicked() {
    this._dialog.open(KfmAppSettingsDialogComponent, {
      width: '600px',
      height: '100%',
      maxHeight: '90vh',
      maxWidth: '90vw',
    });
  }

  /** Use main tenant or last selected tenant */
  private async checkAndInitializeSelectedTenant() {
    const tenantId = await this._authService.getSelectedTenantUId();
    const userTenants = await this._authService.authMandants();

    if (tenantId && userTenants.find((t) => t.id === tenantId)) {
      return;
    }

    this._authService.selectedTenantUId = userTenants[0].id;
  }

  private async checkLicense(): Promise<boolean> {
    if (environment.appStage === AppStage.local) {
      return true;
    }

    const dialogRef = this._dialog.open(KfmSpinnerDialogComponent, {
      data: { text: 'Überprüfe Lizenz...' },
    });

    // Check for licenses
    const licenseValid = await this._licenseService.checkLicense();

    dialogRef.close();

    if (!licenseValid) {
      const infoDialogRef = this._dialog.open(KfmInfoDialogComponent, {
        width: '600px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        disableClose: true,
        data: {
          title: 'Lizenz ungültig',
          text: 'Sie besitzen keine gültige Lizenz für diese Anwendung.',
        },
      });

      infoDialogRef.closed.subscribe(() => {
        this._authService.logout(environment.auth.configId).subscribe(() => {
          console.log('App component - logoffAndRevokeTokens');
        });
      });
    }

    return licenseValid;
  }
}
